import type { CSSProperties } from 'react';
import { useCallback, useRef, useState } from 'react';

import { FloatingPortal, useFloating } from '@floating-ui/react';
import classNames from 'classnames';

import List from '@/icon/List';
import X from '@/icon/X';

import { computeColor } from '../../utils/colors';
import { computeAlignment } from '../../utils/computeAlignment';

import { useDreamComponentsContext } from '../../dreamComponentProvider/hooks';
import type { NavbarMenuBurgerElementProps } from '../../navbarElement/Navbar.type';
import * as styles from '../Navbar.css';
import { NAVBAR_MENU_BURGER_DEFAULTS_V2 } from './NavbarMenuBurger.constants';
import * as v2Styles from './NavbarMenuBurger.css';

export function NavbarMenuBurgerV2(props: NavbarMenuBurgerElementProps) {
  const { element, className, attributes = {}, children, onClick, isInEditor, isLive, isFixed } = props;
  const {
    color,
    size,
    customAttributes,
    gap,
    contentPadding,
    contentBackgroundColor,
    contentBorder,
    contentBorderColor,
    contentBorderWidth,
    contentBorderStyle,
    contentBorderRadius,
    itemsAlignment,
  } = element.attrs || {};

  const { refs, floatingStyles, y } = useFloating({
    strategy: isLive === false || isFixed ? 'fixed' : 'absolute',
    placement: 'bottom-end',
    transform: false,
  });

  // The open state is the host's either way: by prop where one renders the burger directly, or by
  // `navbarBurger` where it renders a whole serialized navbar and has no props to hand down.
  const { navbarBurger } = useDreamComponentsContext();
  const hostOpen = attributes['data-is-open'];
  const isOpen = hostOpen !== undefined ? hostOpen === true : Boolean(navbarBurger?.isOpen);
  const toggle = navbarBurger && (() => navbarBurger.setIsOpen(!isOpen));

  // The menu has to land in the burger's own document — a preview renders inside an iframe, and
  // the portal would otherwise drop it into the surrounding app document. Set synchronously:
  // `root={null}` makes FloatingPortal render nothing, so deferring this to the frame below would
  // leave the menu out of the DOM until it runs, and out of it entirely in a background tab.
  const [portalRoot, setPortalRoot] = useState<HTMLElement | null>(null);
  const stopPositionCheckRef = useRef<(() => void) | null>(null);

  // Attach both reference and position reference
  const setButtonRef = useCallback(
    (node: HTMLButtonElement | null) => {
      setPortalRoot(node?.ownerDocument.body ?? null);
      stopPositionCheckRef.current?.();

      let positionObserver: ResizeObserver | undefined;
      const frame = requestAnimationFrame(() => {
        refs.setReference(node);

        const navbarAncestor = node?.closest<HTMLElement>('.navbar-list');
        const ownerWindow = node?.ownerDocument.defaultView;
        if (navbarAncestor && ownerWindow) {
          // Reading position before layout has run forces a reflow, and a ResizeObserver callback runs after it.
          positionObserver = new ownerWindow.ResizeObserver((_entries, observer) => {
            observer.disconnect();
            // Ensure it can act as a containing block
            if (getComputedStyle(navbarAncestor).position === 'static') {
              navbarAncestor.style.position = 'relative';
            }
            refs.setPositionReference(navbarAncestor);
          });
          positionObserver.observe(navbarAncestor);
        }
      });
      stopPositionCheckRef.current = () => {
        cancelAnimationFrame(frame);
        positionObserver?.disconnect();
      };
    },
    [refs],
  );

  const RenderedDropdown = (
    <div
      ref={refs.setFloating}
      style={
        {
          // Floating popup related
          ...floatingStyles,
          top: `${y}px`,
          left: '0px',
          zIndex: 9999,
          '--content-max-height': `calc(100dvh - ${y}px)`,
          // Styling related
          '--content-gap': gap || NAVBAR_MENU_BURGER_DEFAULTS_V2.gap,
          '--content-padding': contentPadding || NAVBAR_MENU_BURGER_DEFAULTS_V2.padding,
          '--content-background': contentBackgroundColor,
          '--content-border': contentBorder,
          '--content-border-color': contentBorderColor,
          '--content-border-width': contentBorderWidth,
          '--content-border-style': contentBorderStyle,
          '--content-border-radius': contentBorderRadius,
          '--content-items-alignment': computeAlignment(itemsAlignment),
        } as CSSProperties
      }
    >
      {children}
    </div>
  );

  return (
    <>
      <button
        ref={setButtonRef}
        type="button"
        aria-label="Menu"
        onClick={onClick ?? toggle}
        {...customAttributes}
        {...attributes}
        data-is-open={isOpen}
        className={classNames(styles.navbarMenuBurgerV2, className, customAttributes?.className)}
        style={
          {
            '--text-color': computeColor(color ?? NAVBAR_MENU_BURGER_DEFAULTS_V2.color),
          } as CSSProperties
        }
      >
        <List size={size || NAVBAR_MENU_BURGER_DEFAULTS_V2.size} weight="bold" className={v2Styles.openIconV2} />
        <X size={size || NAVBAR_MENU_BURGER_DEFAULTS_V2.size} weight="bold" className={v2Styles.closeIconV2} />
      </button>

      {isInEditor ? RenderedDropdown : <FloatingPortal root={portalRoot}>{RenderedDropdown}</FloatingPortal>}
    </>
  );
}
